import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import MainLayout from '../../components/layout/MainLayout';
import ObituaryForm from '../../components/obituary/ObituaryForm';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { fetchObituaries } from '../../utils/api';

/**
 * ObituaryEditPage - 부고 수정 페이지
 * 기존 부고 데이터를 불러와 수정할 수 있는 페이지
 */
const ObituaryEditPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [formData, setFormData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 부고 데이터 불러오기
  useEffect(() => {
    const loadObituary = async () => {
      try {
        setIsLoading(true);
        const obituaries = await fetchObituaries();
        const obituary = obituaries.find(o => o.id === parseInt(id, 10));

        if (!obituary) {
          showToast('부고를 찾을 수 없습니다.', 'error');
          navigate('/obituaries');
          return;
        }

        // 소유권 검증 (B8): 본인 부고가 아니면 접근 차단 후 상세로 리다이렉트
        if (!user || user.id !== obituary.userId) {
          showToast('본인이 작성한 부고만 수정할 수 있습니다.', 'error');
          navigate(`/obituaries/${id}`, { replace: true });
          return;
        }

        // 날짜 형식 변환 (yyyy-MM-dd)
        const formattedData = {
          ...obituary,
          deceasedBirthDate: obituary.deceasedBirthDate
            ? format(new Date(obituary.deceasedBirthDate), 'yyyy-MM-dd')
            : '',
          deceasedDeathDate: obituary.deceasedDeathDate
            ? format(new Date(obituary.deceasedDeathDate), 'yyyy-MM-dd')
            : '',
          departureDate: obituary.departureDate
            ? format(new Date(obituary.departureDate), 'yyyy-MM-dd')
            : '',
        };

        setFormData(formattedData);
        setOriginalData(formattedData);
      } catch (error) {
        console.error('Failed to load obituary:', error);
        showToast('부고를 불러오는데 실패했습니다.', 'error');
        navigate('/obituaries');
      } finally {
        setIsLoading(false);
      }
    };

    loadObituary();
  }, [id, navigate, showToast, user]);

  // 폼 데이터 변경 핸들러
  const handleChange = (newData) => {
    setFormData(newData);
    // 해당 필드 에러 초기화
    const changedField = Object.keys(newData).find(key => newData[key] !== formData[key]);
    if (changedField && errors[changedField]) {
      const newErrors = { ...errors };
      delete newErrors[changedField];
      setErrors(newErrors);
    }
  };

  // 유효성 검사
  const validateForm = () => {
    const newErrors = {};

    // 고인 정보
    if (!formData.deceasedName?.trim()) {
      newErrors.deceasedName = '고인명을 입력해주세요.';
    }
    if (!formData.deceasedBirthDate) {
      newErrors.deceasedBirthDate = '생년월일을 입력해주세요.';
    }
    if (!formData.deceasedDeathDate) {
      newErrors.deceasedDeathDate = '사망일을 입력해주세요.';
    }
    if (!formData.relationship) {
      newErrors.relationship = '고인과의 관계를 선택해주세요.';
    }

    // 생년월일 < 사망일 검증
    if (formData.deceasedBirthDate && formData.deceasedDeathDate) {
      if (new Date(formData.deceasedBirthDate) > new Date(formData.deceasedDeathDate)) {
        newErrors.deceasedDeathDate = '사망일은 생년월일 이후여야 합니다.';
      }
    }

    // 장례 정보
    if (!formData.funeralHome?.trim()) {
      newErrors.funeralHome = '장례식장명을 입력해주세요.';
    }
    if (!formData.funeralAddress?.trim()) {
      newErrors.funeralAddress = '장례식장 주소를 입력해주세요.';
    }
    if (!formData.funeralRoom?.trim()) {
      newErrors.funeralRoom = '호실을 입력해주세요.';
    }
    if (!formData.departureDate) {
      newErrors.departureDate = '발인일을 입력해주세요.';
    }
    if (!formData.departureTime) {
      newErrors.departureTime = '발인시간을 입력해주세요.';
    }

    // 발인일 > 사망일 검증
    if (formData.deceasedDeathDate && formData.departureDate) {
      if (new Date(formData.departureDate) < new Date(formData.deceasedDeathDate)) {
        newErrors.departureDate = '발인일은 사망일 이후여야 합니다.';
      }
    }

    // 상주 정보
    if (!formData.chiefMourner?.trim()) {
      newErrors.chiefMourner = '상주명을 입력해주세요.';
    }
    if (!formData.chiefMournerPhone?.trim()) {
      newErrors.chiefMournerPhone = '상주 연락처를 입력해주세요.';
    } else {
      const phonePattern = /^01[0-9]-?\d{3,4}-?\d{4}$/;
      if (!phonePattern.test(formData.chiefMournerPhone.replace(/-/g, ''))) {
        newErrors.chiefMournerPhone = '올바른 전화번호 형식이 아닙니다.';
      }
    }
    if (!formData.chiefMournerRelationship?.trim()) {
      newErrors.chiefMournerRelationship = '상주 관계를 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 취소
  const handleCancel = () => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);

    if (hasChanges) {
      if (window.confirm('수정 중인 내용이 저장되지 않습니다. 취소하시겠습니까?')) {
        navigate(`/obituaries/${id}`);
      }
    } else {
      navigate(`/obituaries/${id}`);
    }
  };

  // 미리보기
  const handlePreview = () => {
    if (validateForm()) {
      setShowPreview(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      showToast('필수 항목을 모두 입력해주세요.', 'error');
    }
  };

  // 제출
  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowPreview(false);
      showToast('필수 항목을 모두 입력해주세요.', 'error');
      return;
    }

    try {
      // Mock API 호출 (실제로는 PUT 요청)
      const updatedObituary = {
        ...formData,
        updatedAt: new Date().toISOString(),
      };

      console.log('Updating obituary:', updatedObituary);

      showToast('부고가 성공적으로 수정되었습니다.', 'success');

      setTimeout(() => {
        navigate(`/obituaries/${id}`);
      }, 1000);
    } catch (error) {
      console.error('Failed to update obituary:', error);
      showToast('부고 수정에 실패했습니다.', 'error');
    }
  };

  // 미리보기 렌더링
  const renderPreview = () => {
    if (!formData) return null;

    const formattedDepartureDate = formData.departureDate
      ? format(new Date(formData.departureDate), 'yyyy년 MM월 dd일 (E)', { locale: ko })
      : '';

    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-primary-900 mb-2">부고</h2>
          <div className="w-16 h-1 bg-primary-500 mx-auto mb-6" />
        </div>

        <Card>
          <div className="space-y-4">
            {/* 고인 정보 */}
            <div className="text-center border-b pb-4">
              <h3 className="text-xl font-bold text-primary-900 mb-2">
                故 {formData.deceasedName}
              </h3>
              <p className="text-sm text-gray-600">
                {formData.chiefMourner} {formData.chiefMournerRelationship}의 {formData.relationship}
              </p>
            </div>

            {/* 장례식장 정보 */}
            <div className="space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">빈소</span>
                <span className="text-gray-900">{formData.funeralHome} {formData.funeralRoom}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">주소</span>
                <span className="text-gray-900">{formData.funeralAddress}</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">발인</span>
                <span className="text-gray-900">{formattedDepartureDate} {formData.departureTime}</span>
              </div>
            </div>

            {/* 상주 정보 */}
            <div className="border-t pt-4 space-y-2 text-sm">
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">상주</span>
                <span className="text-gray-900">{formData.chiefMourner} ({formData.chiefMournerRelationship})</span>
              </div>
              <div className="flex">
                <span className="w-24 font-semibold text-gray-700">연락처</span>
                <span className="text-gray-900">{formData.chiefMournerPhone}</span>
              </div>

              {/* 추가 상주 */}
              {formData.additionalMourners && formData.additionalMourners.length > 0 && (
                <div className="flex">
                  <span className="w-24 font-semibold text-gray-700">추가상주</span>
                  <div className="space-y-1">
                    {formData.additionalMourners.map((mourner, index) => (
                      <div key={index} className="text-gray-900">
                        {mourner.name} ({mourner.relationship})
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* 미리보기 액션 */}
        <div className="flex gap-3 justify-center max-w-md mx-auto">
          <Button
            variant="outline"
            onClick={() => setShowPreview(false)}
            className="flex-1"
          >
            수정
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="flex-1"
          >
            저장
          </Button>
        </div>
      </div>
    );
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center">
            <p className="text-gray-600">부고를 불러오는 중...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 데이터 없음
  if (!formData) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="text-center">
            <p className="text-gray-600">부고를 찾을 수 없습니다.</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  // 미리보기 모드
  if (showPreview) {
    return (
      <MainLayout>
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-primary-900 mb-2">
              미리보기
            </h1>
            <p className="text-gray-600">
              수정 내용을 확인하고 저장해주세요.
            </p>
          </div>

          {renderPreview()}
        </div>
      </MainLayout>
    );
  }

  // 폼 입력 모드
  return (
    <MainLayout>
      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary-900 mb-2">
            부고 수정
          </h1>
          <p className="text-gray-600">
            부고 정보를 수정하고 미리보기로 확인해주세요.
          </p>
        </div>

        {/* Form Sections in Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* 섹션 1: 고인 정보 */}
          <Card className="lg:col-span-1">
            <h2 className="text-xl font-bold text-primary-900 mb-4 pb-3 border-b">
              1. 고인 정보
            </h2>
            <ObituaryForm
              formData={formData}
              onChange={handleChange}
              errors={errors}
              step="deceased"
            />
          </Card>

          {/* 섹션 2: 장례 정보 */}
          <Card className="lg:col-span-1">
            <h2 className="text-xl font-bold text-primary-900 mb-4 pb-3 border-b">
              2. 장례 정보
            </h2>
            <ObituaryForm
              formData={formData}
              onChange={handleChange}
              errors={errors}
              step="funeral"
            />
          </Card>

          {/* 섹션 3: 상주 정보 */}
          <Card className="lg:col-span-1">
            <h2 className="text-xl font-bold text-primary-900 mb-4 pb-3 border-b">
              3. 상주 정보
            </h2>
            <ObituaryForm
              formData={formData}
              onChange={handleChange}
              errors={errors}
              step="mourner"
            />
          </Card>
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-center max-w-2xl mx-auto">
          <Button
            variant="outline"
            onClick={handleCancel}
            className="px-8"
          >
            취소
          </Button>
          <Button
            variant="primary"
            onClick={handlePreview}
            className="px-8"
          >
            미리보기
          </Button>
        </div>

        {/* Info */}
        <div className="mt-8 p-6 bg-accent-50 rounded-lg border border-accent-200 max-w-2xl mx-auto">
          <p className="text-sm text-gray-700 text-center">
            <span className="font-semibold">안내:</span> 수정하지 않은 정보는 기존 값이 유지됩니다.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default ObituaryEditPage;
